import { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Divider, Paper, Skeleton, Typography } from "@mui/material";
import { AccountBalanceOutlined, BookmarkBorder, Groups } from "@mui/icons-material";
import {
  ButtonStartExpediente,
  ChipEstadoExpediente,
  DocumentList,
  ExpGroup,
  PageHeader,
  StepActions,
  TabBofuV2,
} from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setBofuTabEmpresaDocument } from "@store/slices/bofuSlice";
import { validateRequiredDocumentsAreUploaded } from "@components/TabBofu/functions";
import { BofuTabDocument } from "@interfaces/response";
import { formatCurrency, isFormStatusEditable } from "@helpers/funciones";
import { PATHS_EXPEDIENTE_V2 } from "@helpers/constantes";

const PATH_SOLICITUD = PATHS_EXPEDIENTE_V2.informacionSolicitud.path;
const PATH_REFERENCIAS = PATHS_EXPEDIENTE_V2.referencias.path;
const PATH_PERSONAS = PATHS_EXPEDIENTE_V2.personas.path;

const PATH_INFO_BANCARIA = PATHS_EXPEDIENTE_V2.informacionBancaria.path;

/** Título del proceso con su estado al margen derecho. */
const TITULO_SX = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" };

/** `.proc-row` del diseño: la franja con el icono, el dato del proceso y su acción. */
const FRANJA_SX = {
  display: "flex",
  alignItems: "center",
  gap: "14px",
  p: "10px 12px 10px 14px",
  border: "1px solid #E4E7EC",
  borderRadius: "10px",
  bgcolor: "#F4F6F9",
};

const ICONO_SX = {
  width: 36,
  height: 36,
  borderRadius: "8px",
  bgcolor: "primary.base",
  color: "primary.main",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const META_SX = {
  flex: 1,
  minWidth: 0,
  color: "text.secondary",
  "& strong": { color: "primary.dark", fontWeight: 600 },
};

/** Un documento cuenta como cargado cuando tiene archivo; los múltiples, con al menos uno. */
const tieneArchivo = (docItem: BofuTabDocument) =>
  docItem.multiple ? docItem.list_values.some(Boolean) : Boolean(docItem.file_value);

export const ExpEmpresa = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const application = useSelector((state: RootState) => state.register.application);
  const bofuTabs = useSelector((state: RootState) => state.bofu.bofu_tabs_obj);
  const bofuLoading = useSelector((state: RootState) => state.bofu.isLoading);

  const monto = application.approved_amount || application.loan_amount;
  const plazo = application.approved_term || application.loan_term;

  const documentos = bofuTabs?.empresa?.documents || [];
  const cargados = documentos.filter(tieneArchivo).length;

  return (
    <Box>
      <PageHeader eyebrow="Expediente digital" title="Documentos de la empresa" />

      <TabBofuV2 active="empresa" />

      {bofuLoading ? (
        <Skeleton variant="rectangular" animation="wave" sx={{ height: 220, borderRadius: "12px" }} />
      ) : (
        <Fragment>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <ExpGroup num="A" title="Procesos guiados" meta="3 procesos" />

              <Paper variant="lendia_paper" sx={{ p: "18px 20px" }}>
                <Box sx={TITULO_SX}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, letterSpacing: "-0.015em", color: "primary.dark" }}
                  >
                    Información de la solicitud
                  </Typography>
                  <ChipEstadoExpediente formStatus={application.loan_application_information_status} />
                </Box>
                <Typography variant="body2" sx={{ color: "text.disabled", lineHeight: 1.55, mt: "4px" }}>
                  Proporciona tu contacto, transaccionales y declaraciones regulatorias.
                </Typography>
                <Box sx={{ ...FRANJA_SX, mt: "14px" }}>
                  <Box sx={ICONO_SX}>
                    <BookmarkBorder sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography variant="body2" sx={META_SX}>
                    <strong>{formatCurrency(monto || 0)} MXN</strong> · {plazo || 0} meses
                  </Typography>
                  <ButtonStartExpediente
                    onClick={() => navigate(PATH_SOLICITUD)}
                    disabled={!isFormStatusEditable(application.loan_application_information_status)}
                    data-testid="expediente-empresa-solicitud-button"
                  >
                    Completar información
                  </ButtonStartExpediente>
                </Box>

                <Divider sx={{ my: "18px" }} />

                <Box sx={TITULO_SX}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, letterSpacing: "-0.015em", color: "primary.dark" }}
                  >
                    Referencias comerciales
                  </Typography>
                  <ChipEstadoExpediente formStatus={application.references_status} />
                </Box>
                <Typography variant="body2" sx={{ color: "text.disabled", lineHeight: 1.55, mt: "4px" }}>
                  Clientes y proveedores que podamos contactar para validar tu operación.
                </Typography>
                <Box sx={{ ...FRANJA_SX, mt: "14px" }}>
                  <Box sx={ICONO_SX}>
                    <Groups sx={{ fontSize: 18 }} />
                  </Box>

                  <Typography variant="body2" sx={META_SX}>
                    <strong>Clientes</strong> 1/3 · <strong>Proveedores</strong> 1/3
                  </Typography>
                  <ButtonStartExpediente
                    onClick={() => navigate(PATH_REFERENCIAS)}
                    disabled={!isFormStatusEditable(application.references_status)}
                    data-testid="expediente-empresa-referencias-button"
                  >
                    Completar referencias
                  </ButtonStartExpediente>
                </Box>

                <Divider sx={{ my: "18px" }} />

                <Box sx={TITULO_SX}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, letterSpacing: "-0.015em", color: "primary.dark" }}
                  >
                    Información bancaria
                  </Typography>
                  <ChipEstadoExpediente formStatus={application.bank_information_status} />
                </Box>
                <Typography variant="body2" sx={{ color: "text.disabled", lineHeight: 1.55, mt: "4px" }}>
                  Cuenta de la empresa donde depositaremos el crédito y desde donde se domiciliarán los pagos.
                </Typography>
                <Box sx={{ ...FRANJA_SX, mt: "14px" }}>
                  <Box sx={ICONO_SX}>
                    <AccountBalanceOutlined sx={{ fontSize: 18 }} />
                  </Box>

                  {/* Sin dato al margen: la cuenta capturada no está en el store de la solicitud. */}
                  <Typography variant="body2" sx={META_SX} />
                  <ButtonStartExpediente
                    onClick={() => navigate(PATH_INFO_BANCARIA)}
                    disabled={!isFormStatusEditable(application.bank_information_status)}
                    data-testid="expediente-empresa-bancaria-button"
                  >
                    Completar información bancaria
                  </ButtonStartExpediente>
                </Box>
              </Paper>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <ExpGroup
                num="B"
                title="Documentos fiscales y bancarios"
                meta={`${cargados} de ${documentos.length} cargados`}
              />
              <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <DocumentList
                    documents={documentos}
                    onChange={({ document, newValue }) =>
                      dispatch(
                        setBofuTabEmpresaDocument({
                          document,
                          newValue,
                          valueAction: undefined,
                          valueIndex: undefined,
                        }),
                      )
                    }
                    onChangeMultiple={({ document, newValue, valueAction, valueIndex }) =>
                      dispatch(setBofuTabEmpresaDocument({ document, newValue, valueAction, valueIndex }))
                    }
                  />
                </Box>
              </Paper>
            </Box>
          </Box>

          {/*
           * TODO: `validateRequiredDocumentsAreUploaded` (compartida con el expediente legacy) solo
           * mira `file_value`, así que un documento `multiple` obligatorio no bloquea el continuar.
           * TODO: el legacy usa `ButtonContinue`, que además valida el `tab_status` de la sección y
           * dispara `postValidateNextStage` en el último tab; aquí se navega directo a personas.
           */}
          <StepActions
            onNext={() => navigate(PATH_PERSONAS)}
            nextLabel="Continuar a personas relacionadas"
            nextDisabled={bofuLoading || !validateRequiredDocumentsAreUploaded(documentos)}
            nextTestId="expediente-empresa-continue-button"
          />
        </Fragment>
      )}
    </Box>
  );
};
